import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";

// Lightweight in-memory cache for favorite IDs so multiple cards on the same page
// stay in sync without hammering the API.
let cachedIds = null;
const listeners = new Set();

async function loadIds(force = false) {
  if (cachedIds && !force) return cachedIds;
  try {
    const { data } = await api.get("/favorites/ids");
    cachedIds = new Set(data);
  } catch {
    cachedIds = new Set();
  }
  listeners.forEach((cb) => cb(cachedIds));
  return cachedIds;
}

export function refreshFavorites() { return loadIds(true); }
export function clearFavoritesCache() { cachedIds = null; listeners.forEach((cb) => cb(new Set())); }

export default function FavoriteButton({ businessId, className = "", size = 18, compact = false, onToggle }) {
  const { user } = useAuth() || {};
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { setSaved(false); return; }
    const cb = (ids) => setSaved(ids.has(businessId));
    listeners.add(cb);
    loadIds().then((ids) => setSaved(ids.has(businessId)));
    return () => listeners.delete(cb);
  }, [user, businessId]);

  const toggle = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      if (saved) {
        await api.delete(`/favorites/${businessId}`);
        cachedIds?.delete(businessId);
        setSaved(false);
        onToggle?.(false);
      } else {
        await api.post(`/favorites/${businessId}`);
        cachedIds?.add(businessId);
        setSaved(true);
        onToggle?.(true);
      }
      listeners.forEach((cb) => cb(cachedIds || new Set()));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      title={saved ? "Retirer des favoris" : "Ajouter aux favoris"}
      className={`inline-flex items-center gap-1.5 rounded-full transition-all ${
        compact ? "p-2 bg-white/90 hover:bg-white shadow-sm" : "px-3 py-2 bg-white border border-stone-200 hover:bg-stone-50"
      } ${className}`}
      data-testid={`favorite-toggle-${businessId}`}
      aria-pressed={saved}
    >
      <Heart
        width={size}
        height={size}
        className={saved ? "text-lokaso-red" : "text-stone-500"}
        fill={saved ? "#C4291C" : "transparent"}
        strokeWidth={2}
      />
      {!compact && <span className="text-sm font-medium">{saved ? "Sauvegardé" : "Sauvegarder"}</span>}
    </button>
  );
}
